import { IconButton } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import {
  GPU_QUOTA_VALUE_COLUMN_KEYS,
  renderGpuQuotaValueCell,
  type GpuQuotaValueColumnKey,
} from "./gpuQuotaColumns";
import {
  formatGpuLimit,
  formatOptionalGpuCount,
  formatQuotaSchedule,
  formatUsageByType,
} from "./gpuQuotaFormat";
import type { GpuQuotaWorkspaceRow } from "./GpuQuotaTableRow";
import styles from "./GpuQuotaPage.module.css";

// 오른쪽으로 정렬하는 칸. 사람 행의 같은 컬럼이 `align: "right"` 인 칸과 같다.
const RIGHT_ALIGNED_KEYS: ReadonlySet<GpuQuotaValueColumnKey> = new Set([
  "pendingGpus",
  "cost",
  "sessionCount",
]);

// 펼친 사람 행 아래의 하위 행 한 건. c6 DataTable 의 renderRowChild 슬롯에 들어간다. 하위 행
// 셀은 표 컬럼 트랙의 subgrid 다. 그래서 값 칸을 사람 행과 같은 셀로 그려 본 컬럼에 정렬한다.
// 사람 행에 합칠 수 없는 값(uuid, 시간대, 스케줄, 임시 증액, group 상한)과 작업 종류 분해, 요청
// 버튼은 대상 컬럼 아래 캡션 줄에 둔다.
export function GpuQuotaWorkspaceChild({
  workspace,
  maxGpuHours,
  canRequest,
  onRequest,
}: {
  workspace: GpuQuotaWorkspaceRow;
  // 사용량 막대의 분모. 사람 행과 같은 값을 써야 두 막대의 길이를 비교할 수 있다.
  maxGpuHours: number;
  // 그 workspace 로 한도 상향을 요청할 수 있는지 나타낸다(선점자 본인 또는 admin). 서버가 접수할 때
  // 다시 판정하므로 표시용이다. 목적은 버튼을 눌러야 403 을 알게 되는 UX 를 피하는 것이다.
  canRequest: boolean;
  onRequest: () => void;
}): ReactNode {
  const usageByType = formatUsageByType(workspace.usageByType);
  return (
    <span className={styles.detailLine}>
      <span className={styles.detailTarget}>
        <span className={styles.workspaceName}>{workspace.displayName}</span>
      </span>
      {GPU_QUOTA_VALUE_COLUMN_KEYS.map((key, position) => (
        <span
          key={key}
          className={
            RIGHT_ALIGNED_KEYS.has(key)
              ? `${styles.detailValue} ${styles.detailValueRight}`
              : styles.detailValue
          }
          // 대상이 1번 트랙이다. 값 칸은 목록 순서대로 그 다음 트랙에 온다.
          style={{ gridColumn: position + 2 }}
        >
          {renderGpuQuotaValueCell(key, { values: workspace, hasQuota: true, maxGpuHours })}
        </span>
      ))}
      <span className={styles.detailCaptionRow}>
        <span className={styles.detailCaption}>
          <span className={styles.workspaceId}>{workspace.workspaceId}</span>
          <span>{formatQuotaSchedule(workspace.schedule, workspace.timezone)}</span>
          {workspace.gpuLimit === null ? null : (
            <span>{`기준 한도 ${formatGpuLimit(workspace.gpuLimit, workspace.gpuLimit)}`}</span>
          )}
          {/* 유효 한도가 스케줄 한도보다 높은 이유다. 승인된 한도 요청이 이 값을 만든다. */}
          {workspace.activeGrantGpus === null ? null : (
            <span>{`임시 증액 ${formatOptionalGpuCount(workspace.activeGrantGpus)}`}</span>
          )}
          {workspace.groupGpuMaxLimit === null ? null : (
            <span>{`그룹 상한 ${formatOptionalGpuCount(workspace.groupGpuMaxLimit)}`}</span>
          )}
          {usageByType === null ? null : <span>{usageByType}</span>}
        </span>
        {canRequest ? (
          <IconButton text="한도 요청" size="sm" variant="surface" onClick={onRequest} />
        ) : null}
      </span>
    </span>
  );
}
